<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useField } from 'vee-validate'
import { AlertCircle } from 'lucide-vue-next'
import type { FormField } from '../../types/formTypeStructure'
import FormFieldWrapper from '../../FormFieldWrapper.vue'
import { getLocalizedText } from '../../utils/formHelpers'

interface Props {
  field: FormField & { fieldType: 'TIP_TAP_DOC_TEXT' }
  name?: string
  currentLang?: string
  defaultValue?: string
  className?: string
}

const props = defineProps<Props>()
const fieldName = props.name ?? props.field.fieldName.toString()
const label = computed(() => getLocalizedText(props.field.label, props.currentLang ?? 'fr'))
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))
const placeholder = computed(() => getLocalizedText(props.field.placeholder, props.currentLang ?? 'fr') || 'Écrivez votre texte ici...')

const {
  value,
  errorMessage,
  handleChange,
  setTouched
} = useField<string>(fieldName, undefined, {
  initialValue: props.defaultValue ?? ''
})

// Version basique : textarea amélioré
// À compléter plus tard avec TipTap Editor complet pour Vue
const textareaRef = ref<HTMLTextAreaElement | null>(null)

const handleInput = (e: Event) => {
  const target = e.target as HTMLTextAreaElement
  handleChange(target.value)
  setTouched(true)
}
</script>

<template>
  <div class="tiptap-field-container">
    <!-- Label dupliqué au-dessus -->
    <div class="tiptap-field-label-container">
      <span class="tiptap-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="props.field" :current-lang="props.currentLang">
      <div :class="['border-2 border-gray-300 rounded-lg overflow-hidden', props.className]">
        <!-- Note: Version basique avec textarea amélioré -->
        <!-- À compléter plus tard avec TipTap Editor complet pour Vue -->
        <textarea
          ref="textareaRef"
          :value="value"
          :placeholder="placeholder"
          @input="handleInput"
          class="w-full p-4 min-h-48 resize-y border-none outline-none focus:ring-2 focus:ring-[#04406E] rounded-lg text-gray-800 placeholder-gray-400"
          rows="10"
        />
      </div>
      <p v-if="errorMessage" class="text-sm text-red-500 mt-1">{{ errorMessage }}</p>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.tiptap-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.tiptap-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.tiptap-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}
</style>

